import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { motion, type HTMLMotionProps } from 'motion/react';

import { cn, defaultSpring } from '@/lib/utils.ts';
import { forwardRef } from 'react';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-blue-600 text-primary-foreground shadow-xs hover:bg-blue-600/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        subtle: 'bg-secondary/50 text-secondary-foreground shadow-xs hover:bg-secondary/70',
        pill: 'rounded-full',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants>,
    Omit<
      HTMLMotionProps<'button'>,
      keyof React.ButtonHTMLAttributes<HTMLButtonElement> | keyof VariantProps<typeof buttonVariants>
    > {
  // Allow motion props, omitting conflicts
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const classNames = cn(buttonVariants({ variant, size, className }));

    if (asChild) {
      return <Slot ref={ref} className={classNames} {...props} />;
    }

    return (
      <motion.button
        ref={ref}
        className={classNames}
        whileTap={{ scale: 0.95, ...((props as any).whileTap || {}) }}
        transition={defaultSpring}
        /* The type conflict between React's HTML attributes and Framer Motion's props
         * is complex. Casting to `any` here to unblock, as the props are compatible
         * at runtime. */
        {...(props as any)}
      />
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
